import React, { useEffect, useState } from 'react';
import { FaPen, FaTrashAlt, FaCheck, FaMoneyBillWave, FaBan } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import {
  getDepensesStats, getDepenses, createDepense, updateDepense, deleteDepense,
  validerDepense, payerDepense, annulerDepense, getDepensesCategories,
  createDepenseCategorie, getDepensesSynthese, getDepensesEvolution, getStations
} from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line
} from 'recharts';

const GLOBAL_ROLES = ['superadmin', 'admin_siege', 'comptable', 'financier', 'fiscaliste', 'auditeur'];
const CHART_COLORS = ['#004A97', '#ED0000', '#FF6B35', '#78BE20', '#9c27b0', '#2196f3', '#ff9800', '#607d8b'];
const formatMontant = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n)) + ' FCFA';

const STATUT_STYLES: Record<string, { bg: string; color: string; label: string }> = {
  brouillon: { bg: '#9e9e9e', color: '#fff', label: 'Brouillon' },
  validee: { bg: '#1976d2', color: '#fff', label: 'Validee' },
  payee: { bg: '#4caf50', color: '#fff', label: 'Payee' },
  annulee: { bg: '#e53935', color: '#fff', label: 'Annulee' },
};

const MODES_PAIEMENT = [
  { value: 'especes', label: 'Especes' },
  { value: 'carte', label: 'Carte' },
  { value: 'virement', label: 'Virement' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'mobile', label: 'Mobile' },
];

const RECURRENCES = [
  { value: 'ponctuelle', label: 'Ponctuelle' },
  { value: 'mensuelle', label: 'Mensuelle' },
  { value: 'trimestrielle', label: 'Trimestrielle' },
  { value: 'annuelle', label: 'Annuelle' },
];

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

const Depenses: React.FC = () => {
  const { user } = useAuth();
  const isGlobal = GLOBAL_ROLES.includes(user?.role || '');
  const [tab, setTab] = useState('overview');
  const [stationFilter, setStationFilter] = useState('');
  const [stations, setStations] = useState<any[]>([]);

  // Overview
  const [stats, setStats] = useState<any>(null);
  const [evolution, setEvolution] = useState<any[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);

  // Depenses list
  const [depenses, setDepenses] = useState<any[]>([]);
  const [loadingDep, setLoadingDep] = useState(false);
  const [catFilter, setCatFilter] = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [dateDebut, setDateDebut] = useState(firstOfMonth());
  const [dateFin, setDateFin] = useState(today());

  // Categories
  const [categories, setCategories] = useState<any[]>([]);
  const [loadingCat, setLoadingCat] = useState(false);
  const [catForm, setCatForm] = useState({ code: '', libelle: '', type_charge: 'fixe', compte_comptable: '' });

  // Synthese
  const [synthese, setSynthese] = useState<any>(null);
  const [loadingSyn, setLoadingSyn] = useState(false);
  const [synMois, setSynMois] = useState(String(new Date().getMonth() + 1));
  const [synAnnee, setSynAnnee] = useState(String(new Date().getFullYear()));

  // Form create/edit
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    station_id: '', categorie_id: '', date_depense: today(), description: '',
    montant: '', mode_paiement: 'especes', fournisseur: '', numero_piece: '', recurrence: 'ponctuelle'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isGlobal) getStations().then(r => setStations(r.data)).catch(console.error);
    loadCategories();
  }, []);

  useEffect(() => {
    if (tab === 'overview') { loadStats(); loadEvolution(); }
    if (tab === 'depenses') loadDepenses();
    if (tab === 'synthese') loadSynthese();
    if (tab === 'categories') loadCategories();
  }, [tab, stationFilter]);

  useEffect(() => {
    if (tab === 'depenses') loadDepenses();
  }, [catFilter, statutFilter, searchFilter, dateDebut, dateFin]);

  useEffect(() => {
    if (tab === 'synthese') loadSynthese();
  }, [synMois, synAnnee]);

  // Loaders
  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const params: any = {};
      if (stationFilter) params.station_id = stationFilter;
      const res = await getDepensesStats(params);
      setStats(res.data);
    } catch (err) { console.error(err); }
    setLoadingStats(false);
  };

  const loadEvolution = async () => {
    try {
      const params: any = {};
      if (stationFilter) params.station_id = stationFilter;
      const res = await getDepensesEvolution(params);
      setEvolution(res.data);
    } catch (err) { console.error(err); }
  };

  const loadDepenses = async () => {
    setLoadingDep(true);
    try {
      const params: any = {};
      if (stationFilter) params.station_id = stationFilter;
      if (catFilter) params.categorie_id = catFilter;
      if (statutFilter) params.statut = statutFilter;
      if (searchFilter) params.search = searchFilter;
      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;
      const res = await getDepenses(params);
      setDepenses(res.data);
    } catch (err) { console.error(err); }
    setLoadingDep(false);
  };

  const loadCategories = async () => {
    setLoadingCat(true);
    try {
      const res = await getDepensesCategories();
      setCategories(res.data);
    } catch (err) { console.error(err); }
    setLoadingCat(false);
  };

  const loadSynthese = async () => {
    setLoadingSyn(true);
    try {
      const params: any = { mois: synMois, annee: synAnnee };
      if (stationFilter) params.station_id = stationFilter;
      const res = await getDepensesSynthese(params);
      setSynthese(res.data);
    } catch (err) { console.error(err); }
    setLoadingSyn(false);
  };

  // Form handlers
  const openCreateForm = () => {
    setEditMode(false); setEditId(null);
    setFormData({
      station_id: stationFilter, categorie_id: '', date_depense: today(), description: '',
      montant: '', mode_paiement: 'especes', fournisseur: '', numero_piece: '', recurrence: 'ponctuelle'
    });
    setShowForm(true);
  };

  const openEditForm = (dep: any) => {
    setEditMode(true); setEditId(dep.id);
    setFormData({
      station_id: String(dep.station_id || ''), categorie_id: String(dep.categorie_id || ''),
      date_depense: dep.date_depense?.slice(0, 10) || today(), description: dep.description || '',
      montant: String(dep.montant || ''), mode_paiement: dep.mode_paiement || 'especes',
      fournisseur: dep.fournisseur || '', numero_piece: dep.numero_piece || '',
      recurrence: dep.recurrence || 'ponctuelle'
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.montant || !formData.description) return alert('Montant et description sont requis');
    if (isGlobal && !formData.station_id) return alert('Selectionnez une station');
    setSaving(true);
    try {
      const data: any = {
        ...formData,
        montant: Number(formData.montant),
        station_id: formData.station_id ? Number(formData.station_id) : null,
        categorie_id: formData.categorie_id ? Number(formData.categorie_id) : null,
      };
      if (editMode && editId) {
        await updateDepense(editId, data);
      } else {
        await createDepense(data);
      }
      setShowForm(false);
      loadDepenses();
      if (tab === 'overview') { loadStats(); loadEvolution(); }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur');
    }
    setSaving(false);
  };

  // Actions
  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer cette depense ?')) return;
    try {
      await deleteDepense(id);
      loadDepenses();
    } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const handleValider = async (id: number) => {
    if (!window.confirm('Valider cette depense ?')) return;
    try {
      await validerDepense(id);
      loadDepenses();
    } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const handlePayer = async (id: number) => {
    if (!window.confirm('Marquer comme payee ?')) return;
    try {
      await payerDepense(id);
      loadDepenses();
    } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const handleAnnuler = async (id: number) => {
    if (!window.confirm('Annuler cette depense ?')) return;
    try {
      await annulerDepense(id);
      loadDepenses();
    } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const handleAddCategorie = async () => {
    if (!catForm.code || !catForm.libelle) return alert('Code et libelle requis');
    setSaving(true);
    try {
      await createDepenseCategorie(catForm);
      setCatForm({ code: '', libelle: '', type_charge: 'fixe', compte_comptable: '' });
      loadCategories();
    } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
    setSaving(false);
  };

  const tabs = [
    { key: 'overview', label: 'Vue d\'ensemble' },
    { key: 'depenses', label: 'Depenses' },
    { key: 'synthese', label: 'Synthese' },
    { key: 'categories', label: 'Categories' },
  ];

  return (
    <div>
      <div className="page-header">
        <h2>Depenses & Charges</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {isGlobal && (
            <select value={stationFilter} onChange={e => setStationFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}>
              <option value="">Toutes les stations</option>
              {stations.map((s: any) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          )}
          <button className="btn btn-primary" onClick={openCreateForm}>+ Nouvelle Depense</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #eee', marginBottom: 20 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '10px 20px', border: 'none', borderBottom: tab === t.key ? '3px solid #ED0000' : '3px solid transparent',
              background: 'none', cursor: 'pointer', fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? '#ED0000' : '#666', fontSize: 14
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══════ TAB: VUE D'ENSEMBLE ═══════ */}
      {tab === 'overview' && (
        loadingStats ? <div className="loading">Chargement...</div> : stats && (
          <>
            <div className="stats-grid">
              <div className="stat-card" style={{ borderTop: '4px solid #004A97' }}>
                <div className="stat-value">{formatMontant(stats.total_mois || 0)}</div>
                <div className="stat-label">DEPENSES DU MOIS</div>
              </div>
              <div className="stat-card" style={{ borderTop: '4px solid #78BE20' }}>
                <div className="stat-value">{stats.nb_validees || 0}</div>
                <div className="stat-label">VALIDEES</div>
              </div>
              <div className="stat-card" style={{ borderTop: '4px solid #ED0000' }}>
                <div className="stat-value">{stats.nb_en_attente || 0}</div>
                <div className="stat-label">EN ATTENTE</div>
              </div>
              <div className="stat-card" style={{ borderTop: '4px solid #FF6B35' }}>
                <div className="stat-value">{stats.evolution_pourcent != null ? `${stats.evolution_pourcent > 0 ? '+' : ''}${stats.evolution_pourcent}%` : '-'}</div>
                <div className="stat-label">EVOLUTION M/M-1</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
              {/* Repartition par categorie */}
              <div className="card">
                <h3 style={{ color: '#004A97', marginBottom: 16 }}>Repartition par categorie</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={stats.par_categorie || []} cx="50%" cy="50%" outerRadius={100}
                      dataKey="total" nameKey="categorie" label={({ categorie, total }: any) => `${categorie}: ${formatMontant(total)}`}>
                      {(stats.par_categorie || []).map((_: any, i: number) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: any) => formatMontant(Number(v))} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Evolution mensuelle */}
              <div className="card">
                <h3 style={{ color: '#004A97', marginBottom: 16 }}>Evolution mensuelle (12 mois)</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={evolution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: any) => formatMontant(Number(v))} />
                    <Bar dataKey="total" fill="#004A97" radius={[4, 4, 0, 0]} name="Depenses" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )
      )}

      {/* ═══════ TAB: DEPENSES ═══════ */}
      {tab === 'depenses' && (
        <>
          <div className="filters" style={{ marginBottom: 16 }}>
            <input type="text" placeholder="Rechercher..." value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', width: 200 }} />
            <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}>
              <option value="">Toutes categories</option>
              {categories.map((c: any) => <option key={c.id} value={c.id}>{c.libelle}</option>)}
            </select>
            <select value={statutFilter} onChange={e => setStatutFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}>
              <option value="">Tous statuts</option>
              <option value="brouillon">Brouillon</option>
              <option value="validee">Validee</option>
              <option value="payee">Payee</option>
              <option value="annulee">Annulee</option>
            </select>
            <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }} />
            <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }} />
            <span className="badge">{depenses.length} depenses</span>
          </div>

          {loadingDep ? <div className="loading">Chargement...</div> : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th><th>Description</th><th>Categorie</th><th>Station</th>
                    <th>Fournisseur</th><th style={{ textAlign: 'right' }}>Montant</th>
                    <th>Mode</th><th>Statut</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {depenses.length === 0 ? (
                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucune depense trouvee</td></tr>
                  ) : depenses.map((d: any) => {
                    const st = STATUT_STYLES[d.statut] || STATUT_STYLES.brouillon;
                    return (
                      <tr key={d.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{d.date_depense ? new Date(d.date_depense).toLocaleDateString('fr-FR') : '-'}</td>
                        <td style={{ fontWeight: 600, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.description}</td>
                        <td>{d.categorie_libelle || '-'}</td>
                        <td>{d.station_nom || '-'}</td>
                        <td>{d.fournisseur || '-'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMontant(Number(d.montant))}</td>
                        <td style={{ fontSize: 12 }}>{d.mode_paiement || '-'}</td>
                        <td><span className="badge" style={{ background: st.bg, color: st.color, fontSize: 11 }}>{st.label}</span></td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap' }}>
                            {d.statut === 'brouillon' && (
                              <>
                                <button className="btn btn-sm" onClick={() => openEditForm(d)} title="Modifier"><FaPen /></button>
                                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(d.id)} title="Supprimer"><FaTrashAlt /></button>
                                <button className="btn btn-sm btn-primary" onClick={() => handleValider(d.id)} title="Valider"><FaCheck /></button>
                              </>
                            )}
                            {d.statut === 'validee' && (
                              <button className="btn btn-sm btn-primary" onClick={() => handlePayer(d.id)} title="Payer"><FaMoneyBillWave /></button>
                            )}
                            {d.statut !== 'annulee' && d.statut !== 'payee' && (
                              <button className="btn btn-sm btn-warning" onClick={() => handleAnnuler(d.id)} title="Annuler"><FaBan /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ═══════ TAB: SYNTHESE ═══════ */}
      {tab === 'synthese' && (
        <>
          <div className="filters" style={{ marginBottom: 16 }}>
            <select value={synMois} onChange={e => setSynMois(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}>
              {['Jan','Fev','Mar','Avr','Mai','Jun','Jul','Aou','Sep','Oct','Nov','Dec'].map((m, i) =>
                <option key={i+1} value={i+1}>{m}</option>
              )}
            </select>
            <select value={synAnnee} onChange={e => setSynAnnee(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}>
              {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {loadingSyn ? <div className="loading">Chargement...</div> : synthese && (
            <div className="card">
              <h3 style={{ color: '#004A97', marginBottom: 16 }}>
                Synthese par categorie - {['Jan','Fev','Mar','Avr','Mai','Jun','Jul','Aou','Sep','Oct','Nov','Dec'][Number(synMois) - 1]} {synAnnee}
              </h3>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Categorie</th><th>Type</th>
                      <th style={{ textAlign: 'right' }}>Nb depenses</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                      <th style={{ textAlign: 'right' }}>% du total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(!synthese.categories || synthese.categories.length === 0) ? (
                      <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucune donnee pour cette periode</td></tr>
                    ) : synthese.categories.map((c: any, i: number) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{c.categorie || 'Non categorisee'}</td>
                        <td>{c.type_charge || '-'}</td>
                        <td style={{ textAlign: 'right' }}>{c.nb_depenses}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMontant(Number(c.total))}</td>
                        <td style={{ textAlign: 'right' }}>
                          {synthese.total_general ? ((Number(c.total) / Number(synthese.total_general)) * 100).toFixed(1) + '%' : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {synthese.total_general > 0 && (
                    <tfoot>
                      <tr style={{ fontWeight: 700, background: '#f5f5f5' }}>
                        <td colSpan={2}>TOTAL</td>
                        <td style={{ textAlign: 'right' }}>{synthese.nb_total || '-'}</td>
                        <td style={{ textAlign: 'right', color: '#004A97' }}>{formatMontant(Number(synthese.total_general))}</td>
                        <td style={{ textAlign: 'right' }}>100%</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════ TAB: CATEGORIES ═══════ */}
      {tab === 'categories' && (
        <>
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ color: '#004A97', marginBottom: 16 }}>Ajouter une categorie</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Code *</label>
                <input type="text" value={catForm.code} placeholder="ex: CARB"
                  onChange={e => setCatForm(p => ({ ...p, code: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Libelle *</label>
                <input type="text" value={catForm.libelle} placeholder="ex: Carburant"
                  onChange={e => setCatForm(p => ({ ...p, libelle: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Type de charge</label>
                <select value={catForm.type_charge} onChange={e => setCatForm(p => ({ ...p, type_charge: e.target.value }))}>
                  <option value="fixe">Fixe</option>
                  <option value="variable">Variable</option>
                </select>
              </div>
              <div className="form-group">
                <label>Compte comptable</label>
                <input type="text" value={catForm.compte_comptable} placeholder="ex: 601000"
                  onChange={e => setCatForm(p => ({ ...p, compte_comptable: e.target.value }))} />
              </div>
            </div>
            <button className="btn btn-primary" onClick={handleAddCategorie} disabled={saving} style={{ marginTop: 8 }}>
              {saving ? 'Enregistrement...' : 'Ajouter la categorie'}
            </button>
          </div>

          {loadingCat ? <div className="loading">Chargement...</div> : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr><th>Code</th><th>Libelle</th><th>Type de charge</th><th>Compte comptable</th></tr>
                </thead>
                <tbody>
                  {categories.length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucune categorie</td></tr>
                  ) : categories.map((c: any) => (
                    <tr key={c.id}>
                      <td><code style={{ fontSize: 13 }}>{c.code}</code></td>
                      <td style={{ fontWeight: 600 }}>{c.libelle}</td>
                      <td><span className="badge" style={{ background: c.type_charge === 'fixe' ? '#1976d2' : '#ff9800', color: '#fff' }}>{c.type_charge}</span></td>
                      <td>{c.compte_comptable || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ═══════ MODAL: FORMULAIRE DEPENSE ═══════ */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Modifier la depense' : 'Nouvelle depense'}</h3>
              <button className="modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {isGlobal && (
                <div className="form-group">
                  <label>Station *</label>
                  <select value={formData.station_id} onChange={e => setFormData(p => ({ ...p, station_id: e.target.value }))}>
                    <option value="">-- Selectionner --</option>
                    {stations.map((s: any) => <option key={s.id} value={s.id}>{s.nom}</option>)}
                  </select>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Categorie</label>
                  <select value={formData.categorie_id} onChange={e => setFormData(p => ({ ...p, categorie_id: e.target.value }))}>
                    <option value="">-- Selectionner --</option>
                    {categories.map((c: any) => <option key={c.id} value={c.id}>{c.libelle}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date depense</label>
                  <input type="date" value={formData.date_depense}
                    onChange={e => setFormData(p => ({ ...p, date_depense: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label>Description *</label>
                <textarea value={formData.description} rows={3}
                  onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                  placeholder="Decrivez la depense..." />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Montant (FCFA) *</label>
                  <input type="number" value={formData.montant} min="0"
                    onChange={e => setFormData(p => ({ ...p, montant: e.target.value }))} placeholder="0" />
                </div>
                <div className="form-group">
                  <label>Mode de paiement</label>
                  <select value={formData.mode_paiement} onChange={e => setFormData(p => ({ ...p, mode_paiement: e.target.value }))}>
                    {MODES_PAIEMENT.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Fournisseur</label>
                  <input type="text" value={formData.fournisseur}
                    onChange={e => setFormData(p => ({ ...p, fournisseur: e.target.value }))} placeholder="Nom du fournisseur" />
                </div>
                <div className="form-group">
                  <label>Numero de piece</label>
                  <input type="text" value={formData.numero_piece}
                    onChange={e => setFormData(p => ({ ...p, numero_piece: e.target.value }))} placeholder="Ref. facture/bon" />
                </div>
              </div>
              <div className="form-group">
                <label>Recurrence</label>
                <select value={formData.recurrence} onChange={e => setFormData(p => ({ ...p, recurrence: e.target.value }))}>
                  {RECURRENCES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setShowForm(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Enregistrement...' : (editMode ? 'Enregistrer' : 'Creer la depense')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Depenses;
